import { Skeleton } from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { ApiProblem } from "@/components/ApiProblem";
import { NotFound } from "@/components/RouteError";
import { useSession } from "@/features/auth/queries";
import { isNotFound, requestErrorMessage } from "@/lib/api";
import { profileQuery } from "../queries";
import { ProfileScreen } from "./ProfileScreen";

/** What a profile id looks like, so a mistyped one is not found without asking. */
export const PROFILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Someone's profile page, by id. */
export function ProfilePage({ profileId }: { profileId: string }) {
  const valid = PROFILE_ID.test(profileId);
  const { data: session } = useSession();
  const profile = useQuery({ ...profileQuery(profileId), enabled: valid });

  if (!valid || isNotFound(profile.error)) return <NotFound />;
  if (profile.error) return <ApiProblem message={requestErrorMessage(profile.error)} />;
  if (!profile.data) return <ProfileSkeleton />;

  const viewerId = session && !session.user.isAnonymous ? session.user.id : null;
  // Keyed by profile, so moving from one person's page to another's starts afresh.
  return <ProfileScreen key={profileId} profile={profile.data} viewerId={viewerId} />;
}

function ProfileSkeleton() {
  return (
    <main aria-busy className="app-reading-width [--reading-width:64rem] pt-4">
      <Skeleton className="aspect-[3/1] w-full rounded-2xl" />
      <div className="px-2 sm:px-6">
        <Skeleton className="-mt-11 size-24 rounded-full ring-4 ring-background" />
        <Skeleton className="mt-3 h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-40" />
        <Skeleton className="mt-6 h-10 w-80" />
      </div>
    </main>
  );
}
